<template>
	<div class="cc-product">
		<div class="cc-product__slider">
			<ProductSlider
				:images="purchasable.images"
				:videos="purchasable.videos"
				:productId="data.id"
			>
				<template v-slot:labels>
					<Labels :labels="labels"></Labels>
				</template>
			</ProductSlider>
		</div>

		<div class="cc-product__details">
			<!-- BRAND -->
			<div class="brands">
				<a :href="data.brand.url">{{ data.brand.title }}</a>
			</div>

			<!-- LABELS -->
			<div class="labels">
				<Labels
					class=""
					:labels="labels"
					:newProduct="newProduct"
					:saleProduct="purchasable.sale"
				></Labels>
			</div>

			<!-- TITLE -->
			<h1>{{ data.title }}</h1>

			<!-- RATING -->
			<div class="subline">
				<Rating
					number="amount"
					:button="true"
					:review="data.rating"
					class="info__rating"
					@scroll-to-reviews="onScrollToReviews"
				></Rating>

				<GoWish />
			</div>

			<!-- PRICE -->
			<div class="product-price">
				<PriceObject :price="purchasable.price" :splitpay="true" />
			</div>

			<!-- DYNAMIC -->
			<ProductVariants
				v-if="data.type == 'product'"
				:variants="data.variants"
				:property="data.variantProperty"
				:selected="purchasable"
				@variant="selectPurchasable"
			></ProductVariants>

			<!-- CART -->
			<ProductAddToCart
				:purchasable="purchasable"
				:product="data"
				:alternatives="alternatives"
			></ProductAddToCart>

			<!-- Accordions -->
			<div class="accordions">
				<Accordion
					:title="translations['Bundle contents']"
					v-if="data.type == 'bundle-internal'"
				>
					<template v-slot:contents>
						<ProductBundles :products="data.products"></ProductBundles>
					</template>
				</Accordion>

				<ProductDetails
					:id="data.id"
					:description="data.description"
					:usage="data.usage"
					:contains="data.contains"
					:review="data.rating"
					:isScrolled="isScrolledToReviews"
				>
				</ProductDetails>
			</div>
		</div>
	</div>
</template>

<script>
//Components
import Accordion from "@/vue/components/objects/Accordion.vue";
import Rating from "@/vue/components/objects/Rating.vue";
import ProductSlider from "@/vue/components/objects/ProductSlider.vue";
import ProductVariants from "@/vue/components/commerce/objects/ProductVariants.vue";
import ProductBundles from "@/vue/components/commerce/objects/ProductBundles.vue";
import ProductDetails from "@/vue/components/commerce/objects/ProductDetails.vue";
import PriceObject from "@/vue/components/objects/PriceObject.vue";
import GoWish from "@/vue/components/commerce/objects/GoWish.vue";
import ProductAddToCart from "./objects/ProductAddToCart.vue";
import request from "@/vue/mixins/request.js";

//Icons
import Reviews from "./Reviews.vue";
import Labels from "../objects/Labels.vue";

export default {
	name: "CommerceProduct",
	mixins: [request],
	props: {
		data: Object,
		alternatives: Array,
		labels: Array,
		newProduct: Boolean,
		sale: Object,
		userId: Number,
	},
	components: {
		//Components
		GoWish,
		Accordion,
		Rating,
		ProductAddToCart,
		ProductSlider,
		Reviews,
		ProductVariants,
		ProductBundles,
		ProductDetails,
		PriceObject,
		Labels,
	},
	data: () => {
		return {
			purchasable: 0,
			isScrolledToReviews: false,
		};
	},
	methods: {
		selectPurchasable(id) {
			this.purchasable = this.data.variants[id];
		},
		onScrollToReviews() {
			this.isScrolledToReviews = true;
		},
	},

	mounted() {
		const formData = new FormData();
		formData.append("action", this.action.addProductRecommendation);
		formData.append("productId", this.data.id);

		this.request(formData).then(() => {});
	},

	created() {
		switch (this.data.type) {
			case "product":
				this.purchasable = this.data.variants[this.data.defaultPurchasableId];
				break;
			case "bundle-external":
				this.purchasable = this.data.variants[this.data.defaultPurchasableId];
				break;
			case "bundle-internal":
				this.purchasable = this.data.purchasable;
				break;
		}
	},
};
</script>

<style lang="scss">
@import "@backend/scss/abstracts/variables.scss";
@import "@backend/scss/abstracts/mixins.scss";

.cc-product {
	@include grid-keep();
	display: flex;
	margin-bottom: 80px;
	margin-top: 30px;
	gap: 40px;

	&__slider {
		flex: 1;
		z-index: 19;
		max-width: 610px;
		min-width: calc(50% - 40px);
	}

	&__details {
		display: flex;
		flex-direction: column;
		flex: 1;
		position: relative;
		overflow: hidden;

		.brands {
			margin-bottom: 12px;

			a {
				text-transform: uppercase;
				color: $c-blue;
				opacity: 0.5;
				@include hover-underline(rgba($c-blue, 0.5));
			}
		}

		.labels {
			display: flex;
			// margin-bottom: 12px;

			.o-labels {
				width: 100%;
				flex-wrap: wrap;
				margin-bottom: 12px;
			}
		}

		h1 {
			margin-bottom: 9px;
			text-wrap: balance;
		}

		.subline {
			margin-bottom: 24px;
			display: flex;

			gap: 15px;

			.c-gowish {
				padding-left: 15px;
				// border-left: 1px solid $c-blue;
				border-left: 1px solid rgba($color: $c-blue, $alpha: 0.3);
				border-left: 1px solid darken(rgba(234, 233, 233, $alpha: 1), 5);
			}
		}

		.product-price {
			margin-bottom: 32px;
			gap: 16px;
			font-size: 24px;
		}

		.cco-productVariants {
			margin-bottom: 32px;
		}
	}
}

@media all and (max-width: 1100px) {
	.cc-product {
		&__slider {
			max-width: 50%;
		}
	}
}

@media all and (max-width: 950px) {
	.cc-product {
		flex-direction: column;
		gap: 24px;

		&__slider {
			max-width: 100%;
		}

		&__details {
			text-align: center;

			.labels {
				display: none;
			}

			.subline {
				display: flex;
				justify-content: center;
				margin-bottom: 16px;
			}

			.product-price {
				width: 100%;
				justify-content: center;

				font-size: $font-base-size;
				gap: 8px;
				margin-bottom: 24px;
			}
		}
	}
}

@media all and (max-width: 450px) {
	.cc-product {
		gap: 8px;
		&__slider {
			// padding-inline: 15px;
		}
		&__details {
			.brands {
				margin-bottom: 2px;
			}
		}
	}
}
</style>
